"use client";

import { EmployerType, registerIndividualEmployer, useUserEmployers } from "@entities/employer";
import { Button } from "@ubud-ui/react-ui-kit";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { useState, useTransition } from "react";
import { toast } from "sonner";

export function IndividualEmployerRegistration({ redirectUrl }: { redirectUrl?: string }) {
  const { isLoading, isError, error, employers, refetch } = useUserEmployers({ limit: 100 });

  const hasIndividualEmployer = employers.some((employer) => employer.employerableType === EmployerType.INDIVIDUAL);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isRegistering, startRegistering] = useTransition();

  const handleRegister = () => {
    setErrorMessage("");
    startRegistering(async () => {
      try {
        const response = await registerIndividualEmployer();

        if (response && typeof response === "object" && "error" in response && response.error) {
          setErrorMessage(
            typeof response.errorDescription === "string"
              ? response.errorDescription
              : typeof response.error === "string"
                ? response.error
                : "Gagal mendaftar sebagai pemberi kerja individu.",
          );
        } else {
          toast.success("Pendaftaran Pemberi Kerja Individu", {
            description: "🎉 Selamat! Pendaftaran sebagai pemberi kerja individu telah berhasil.",
            duration: 3000,
          });
          setTimeout(() => {
            window.location.replace(redirectUrl || "/");
          }, 3000);
        }

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "Terjadi kesalahan saat mendaftar sebagai pemberi kerja individu.");
      }
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 md:p-10">
        <h1 className="text-xl font-bold">Pemberi Kerja Individu</h1>
        <p className="text-sm font-normal text-gray-500 mt-2">Memeriksa data pemberi kerja Anda...</p>
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 md:p-10">
        <h1 className="text-xl font-bold">Pemberi Kerja Individu</h1>
        <p className="text-sm font-normal text-gray-500 mt-2">Gagal memeriksa data pemberi kerja Anda.</p>
        <div className="mt-6 md:mt-8">
          <div className="bg-red-100 rounded-lg px-4 py-3 space-y-3 text-red-600">
            <h2 className="font-semibold text-sm flex items-center gap-2">
              <ExclamationCircleIcon className="size-5 min-w-5" />
              <span>Terjadi kesalahan</span>
            </h2>
            <div className="text-sm">{error instanceof Error ? error.message : "Gagal memuat data pemberi kerja. Silakan coba lagi."}</div>
          </div>
        </div>
        <div className="mt-6">
          <Button type="button" className="w-full text-sm font-bold" onClick={() => refetch()}>
            Coba Lagi
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <h1 className="text-xl font-bold">Pemberi Kerja Individu</h1>
      <p className="text-sm font-normal text-gray-500 mt-2">
        Daftarkan diri Anda sebagai pemberi kerja individu untuk membuka lowongan kerja secara resmi.
      </p>

      <div className="mt-6 md:mt-8 space-y-4">
        {/* Info alert about individual employer */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="size-8 min-w-8 rounded-lg bg-blue-100 inline-flex items-center justify-center text-center">
            <InformationCircleIcon className="size-5 min-w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-sm text-blue-800">Tentang Pemberi Kerja Individu</h4>
            <p className="text-xs text-blue-700 font-normal mt-1">
              Pemberi kerja individu adalah perorangan yang membutuhkan tenaga kerja seperti asisten rumah tangga, pengasuh, sopir pribadi,
              atau kebutuhan pekerjaan perorangan lainnya. Setiap akun hanya dapat mendaftar satu pemberi kerja individu.
            </p>
          </div>
        </div>

        {/* Already exists warning */}
        {hasIndividualEmployer && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <div className="size-8 min-w-8 rounded-lg bg-amber-100 inline-flex items-center justify-center text-center">
              <ExclamationTriangleIcon className="size-5 min-w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-sm text-amber-800">Sudah Terdaftar</h4>
              <p className="text-xs text-amber-700 font-normal mt-1">
                Akun Anda sudah memiliki pemberi kerja individu. Anda tidak dapat mendaftar sebagai pemberi kerja individu lagi karena
                setiap akun hanya diperbolehkan memiliki satu pemberi kerja individu.
              </p>
            </div>
          </div>
        )}

        {/* Registration error */}
        {errorMessage ? (
          <div className="bg-red-100 rounded-lg px-4 py-3 space-y-3 text-red-600">
            <h2 className="font-semibold text-sm flex items-center gap-2">
              <ExclamationCircleIcon className="size-5 min-w-5" />
              <span>Terjadi kesalahan</span>
            </h2>
            <div className="text-sm">{errorMessage}</div>
          </div>
        ) : null}
      </div>

      <div className="mt-6">
        <Button
          type="button"
          className="w-full text-sm font-bold"
          disabled={hasIndividualEmployer || isRegistering}
          onClick={handleRegister}
        >
          {isRegistering ? "Sedang mendaftar..." : hasIndividualEmployer ? "Sudah Terdaftar" : "Daftar sebagai Pemberi Kerja Individu"}
        </Button>
      </div>

      {hasIndividualEmployer && (
        <div className="mt-4 text-center">
          <Button variant="link" className="font-bold text-sm text-gray-500" asChild>
            <a href={redirectUrl || "/"}>
              <span>Kembali</span>
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      aria-hidden="true"
      className="w-8 h-8 text-gray-300 animate-spin fill-primary-600"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
  );
}
